/**
 * Endpoints that REQUIRE `Idempotency-Key` per DEC-006.
 * Patterns are normalized openapi paths (with `{param}` placeholders).
 *
 * Listed explicitly to avoid attaching keys to read-only PATCH/PUT endpoints
 * (e.g. /users/me preferences) where idempotency is implicit by primary key.
 */
const REQUIRE_IDEMPOTENCY: ReadonlyArray<{ method: string; pathPattern: RegExp }> = [
  // Order create / cancel / claim
  { method: 'POST', pathPattern: /^\/orders$/ },
  { method: 'POST', pathPattern: /^\/orders\/[^/]+\/cancel-requests$/ },
  { method: 'POST', pathPattern: /^\/orders\/[^/]+\/claims$/ },
  // Claim user-side cancel (취하) — Slice 5
  { method: 'POST', pathPattern: /^\/claims\/[^/]+\/cancel$/ },
  // Payment intent / confirm / cancel / refund (Slice 3B)
  { method: 'POST', pathPattern: /^\/payments\/intents$/ },
  { method: 'POST', pathPattern: /^\/payments\/confirm$/ },
  { method: 'POST', pathPattern: /^\/payments\/[^/]+\/cancel$/ },
  { method: 'POST', pathPattern: /^\/payments\/[^/]+\/refunds$/ },
  // Pickup confirmation
  { method: 'POST', pathPattern: /^\/pickups\/[^/]+\/confirm$/ },
  // Pickup verify-code (Slice 4) — owner-only code reveal/resend, BE throttles
  // per ADR-025 Option A. Idempotency key prevents accidental double-sends on
  // SMS/EMAIL delivery channels.
  { method: 'POST', pathPattern: /^\/pickups\/[^/]+\/verify-code$/ },
  // Cart writes (idempotent semantics, but FE supplies key for retry safety)
  { method: 'POST', pathPattern: /^\/cart\/items$/ },
  { method: 'PATCH', pathPattern: /^\/cart\/items\/[^/]+$/ },
  { method: 'DELETE', pathPattern: /^\/cart\/items\/[^/]+$/ },
  { method: 'DELETE', pathPattern: /^\/cart$/ },
  // Checkout session — TTL 15분, 같은 키 재요청 시 동일 세션 (DEC-006)
  { method: 'POST', pathPattern: /^\/orders\/checkout-sessions$/ },
  // Admin writes — order ops, settlement approvals, infra scale
  { method: 'POST', pathPattern: /^\/admin\/v1\/orders\/[^/]+\/[^/]+$/ },
  { method: 'POST', pathPattern: /^\/admin\/v1\/settlement\/.+\/approvals$/ },
  { method: 'POST', pathPattern: /^\/admin\/v1\/infra\/(scale-up|scale-down|rollback-deploy)$/ },
  // ID55 수기주문 생성 (order-service admin) — 1-세그먼트라 위 /orders/{id}/{action} 2-세그먼트 패턴과 비충돌.
  { method: 'POST', pathPattern: /^\/admin\/v1\/orders\/manual$/ },
  // Wave2 주문 기본설정 PUT (싱글톤 upsert).
  { method: 'PUT', pathPattern: /^\/admin\/v1\/orders\/policy-settings$/ },
  // ID51_3 장바구니 정책 PUT (싱글톤 upsert, order-service).
  { method: 'PUT', pathPattern: /^\/admin\/v1\/orders\/cart-policy-settings$/ },
  // ID51_3 관심상품/간편구매 정책 PUT (싱글톤 upsert, user-service).
  { method: 'PUT', pathPattern: /^\/admin\/v1\/wishlist\/policy-settings$/ },
  // ID50 배송비조건 관리 (order-service admin shipping fee-policies CRUD).
  { method: 'POST', pathPattern: /^\/admin\/v1\/shipping\/fee-policies$/ },
  { method: 'PUT', pathPattern: /^\/admin\/v1\/shipping\/fee-policies\/[^/]+$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/shipping\/fee-policies\/[^/]+$/ },
  // Admin payment writes — Slice 3B (refund / void) + be-payment V20 수기 환불 기록(manual-refund)
  { method: 'POST', pathPattern: /^\/admin\/v1\/payments\/[^/]+\/(refund|void|manual-refund)$/ },
  // Admin catalog writes — Slice 1 (product CRUD)
  { method: 'POST', pathPattern: /^\/admin\/v1\/products$/ },
  { method: 'PUT', pathPattern: /^\/admin\/v1\/products\/[^/]+$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/products\/[^/]+$/ },
  // ID60 상품 복사 — deep-clone POST. 더블클릭/네트워크 재시도 시 중복 복제 방지.
  { method: 'POST', pathPattern: /^\/admin\/v1\/products\/[^/]+\/copy$/ },
  // ID49 전역 수량할인 정책 PUT (upsert, per-product 아님) — 재시도 안전.
  { method: 'PUT', pathPattern: /^\/admin\/v1\/quantity-discount$/ },
  // ID41 상품 커스텀 폼/동의 항목 PUT (통합 collection-form 전체 교체 upsert).
  { method: 'PUT', pathPattern: /^\/admin\/v1\/products\/[^/]+\/collection-form$/ },
  // ID43 카테고리-상품 매핑 추가/제외/정렬순서 저장.
  { method: 'POST', pathPattern: /^\/admin\/v1\/categories\/[^/]+\/products$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/categories\/[^/]+\/products\/[^/]+$/ },
  { method: 'PUT', pathPattern: /^\/admin\/v1\/categories\/[^/]+\/products\/order$/ },
  // ID23 이벤트 수집(응모) 항목 CUD — catalog 이벤트 하위 리소스. 더블클릭/재시도 시 중복 필드 생성 방지.
  { method: 'POST', pathPattern: /^\/admin\/v1\/events\/[^/]+\/collection-fields$/ },
  { method: 'PUT', pathPattern: /^\/admin\/v1\/events\/[^/]+\/collection-fields\/[^/]+$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/events\/[^/]+\/collection-fields\/[^/]+$/ },
  // ID18 EVER TV 외부 서비스(GA·외부 스크립트) CUD — catalog CMS 리소스. SF 전역 주입 대상이라 중복 등록 방지.
  { method: 'POST', pathPattern: /^\/admin\/v1\/ever-tv\/external-services$/ },
  { method: 'PUT', pathPattern: /^\/admin\/v1\/ever-tv\/external-services\/[^/]+$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/ever-tv\/external-services\/[^/]+$/ },
  // 전시·콘텐츠 (display-content) admin writes — catalog-service.
  // ID44 메인 섹션 순서 reorder, ID45 배너 순서 reorder, ID36 팝업 슬롯 사후수정, ID58 미리보기 토큰 발급.
  { method: 'PUT', pathPattern: /^\/admin\/v1\/home-sections\/reorder$/ },
  { method: 'PUT', pathPattern: /^\/admin\/v1\/home-banners\/reorder$/ },
  // QA-102 메인 히어로 헤드라인 캡션 PUT (싱글톤 upsert, catalog CMS). 더블클릭/재시도 시 중복 저장 방지.
  { method: 'PUT', pathPattern: /^\/admin\/v1\/cms\/home-hero-caption$/ },
  { method: 'PUT', pathPattern: /^\/admin\/v1\/popups\/[^/]+\/slots\/[^/]+$/ },
  { method: 'POST', pathPattern: /^\/admin\/v1\/preview-tokens$/ },
  // Admin pickup writes — Slice 4 (store/slot CRUD + pickup state actions)
  // ADR-025 (pickup code) + DEC-006 (Idempotency) per backoffice-pickup.yaml.
  { method: 'POST', pathPattern: /^\/admin\/v1\/pickups\/stores$/ },
  { method: 'PATCH', pathPattern: /^\/admin\/v1\/pickups\/stores\/[^/]+$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/pickups\/stores\/[^/]+$/ },
  { method: 'POST', pathPattern: /^\/admin\/v1\/pickups\/stores\/[^/]+\/slots$/ },
  { method: 'PATCH', pathPattern: /^\/admin\/v1\/pickups\/slots\/[^/]+$/ },
  {
    method: 'POST',
    pathPattern: /^\/admin\/v1\/pickups\/[^/]+\/(ready|complete|no-show|reissue-code)$/,
  },
  // Admin claim writes — Slice 5 (approve/reject/collect/complete + refund-completed callback).
  // backoffice-claim.yaml RBAC + DEC-006 (Idempotency-Key). `refund-completed`
  // is the payment-service consumer callback and shares the same idempotency
  // requirement (eventId reuse).
  {
    method: 'POST',
    pathPattern: /^\/admin\/v1\/claims\/[^/]+\/(approve|reject|collect|complete|refund-completed)$/,
  },
  // Admin vendor application writes — Slice 7 (Phase 2). DEC-006 (Idempotency-Key)
  // + DEC-016 (single approval). All FSM transitions require key for retry safety.
  {
    method: 'POST',
    pathPattern: /^\/admin\/v1\/vendor-applications\/[^/]+\/(assign|start-review|approve|reject|request-resubmission|issue-credentials)$/,
  },
  // Admin artist writes — Slice 9 (Phase 2). DEC-017 Master Admin only.
  // CRUD + image multipart + alias + group/member — all idempotent (ADR-028 §3).
  { method: 'POST', pathPattern: /^\/admin\/v1\/artists$/ },
  { method: 'PATCH', pathPattern: /^\/admin\/v1\/artists\/[^/]+$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/artists\/[^/]+$/ },
  { method: 'POST', pathPattern: /^\/admin\/v1\/artists\/[^/]+\/images$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/artists\/[^/]+\/images\/[^/]+$/ },
  { method: 'POST', pathPattern: /^\/admin\/v1\/artists\/[^/]+\/aliases$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/artists\/[^/]+\/aliases\/[^/]+$/ },
  { method: 'POST', pathPattern: /^\/admin\/v1\/artist-groups$/ },
  { method: 'POST', pathPattern: /^\/admin\/v1\/artist-groups\/[^/]+\/members$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/artist-groups\/[^/]+\/members\/[^/]+$/ },
  // Funding pledge writes — Slice 11 (Phase 2). DEC-006 (Idempotency-Key) +
  // DEC-019 (auto-close) — POST 트리거가 결제와 동등 idempotency 요건.
  { method: 'POST', pathPattern: /^\/funding\/v1\/campaigns\/[^/]+\/pledges$/ },
  { method: 'POST', pathPattern: /^\/funding\/v1\/pledges\/[^/]+\/cancel$/ },
  // Admin funding writes — Slice 11. Master Admin only.
  { method: 'POST', pathPattern: /^\/admin\/v1\/funding-campaigns$/ },
  { method: 'PATCH', pathPattern: /^\/admin\/v1\/funding-campaigns\/[^/]+$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/funding-campaigns\/[^/]+$/ },
  { method: 'POST', pathPattern: /^\/admin\/v1\/funding-campaigns\/[^/]+\/rewards$/ },
  { method: 'PATCH', pathPattern: /^\/admin\/v1\/funding-campaigns\/[^/]+\/rewards\/[^/]+$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/funding-campaigns\/[^/]+\/rewards\/[^/]+$/ },
  { method: 'POST', pathPattern: /^\/admin\/v1\/funding-campaigns\/[^/]+\/(launch|close)$/ },
  // Admin settlement writes — Slice 14. DEC-021 (cycle/approve/payout) + DEC-010 (2FA on
  // SUPER approval). Generate cycle / approve (1차+2차) / payout / release schedule CRUD.
  {
    method: 'POST',
    pathPattern: /^\/admin\/v1\/settlements\/cycles\/[^/]+\/generate$/,
  },
  { method: 'POST', pathPattern: /^\/admin\/v1\/settlements\/[^/]+\/approve$/ },
  { method: 'POST', pathPattern: /^\/admin\/v1\/settlements\/[^/]+\/payouts$/ },
  { method: 'POST', pathPattern: /^\/admin\/v1\/release-schedules$/ },
  { method: 'PATCH', pathPattern: /^\/admin\/v1\/release-schedules\/[^/]+$/ },
  { method: 'DELETE', pathPattern: /^\/admin\/v1\/release-schedules\/[^/]+$/ },
  // Slice 14 release publish — Outbox `release_schedule.published.v1` 발행. 더블 클릭 /
  // network retry 시 중복 outbox row 방지하려면 Idempotency-Key 필수.
  { method: 'POST', pathPattern: /^\/admin\/v1\/release-schedules\/[^/]+\/publish$/ },
  // User auth writes — Slice 2A (paths/user.yaml). Each requires Idempotency-Key per DEC-006.
  { method: 'POST', pathPattern: /^\/users\/signup$/ },
  { method: 'POST', pathPattern: /^\/users\/check-duplicate$/ },
  { method: 'POST', pathPattern: /^\/users\/login$/ },
  { method: 'POST', pathPattern: /^\/users\/token\/refresh$/ },
  { method: 'POST', pathPattern: /^\/users\/logout$/ },
  { method: 'POST', pathPattern: /^\/users\/password\/change$/ },
  { method: 'POST', pathPattern: /^\/users\/2fa\/verify$/ },
  { method: 'PATCH', pathPattern: /^\/users\/me$/ },
];

export const requiresIdempotencyKey = (method: string, path: string): boolean => {
  const m = method.toUpperCase();
  return REQUIRE_IDEMPOTENCY.some((r) => r.method === m && r.pathPattern.test(path));
};

/**
 * Endpoints whose Idempotency-Key MUST survive page reload (DECISION-W2-10 §3,
 * W3-T2 SF-3). The browser leaves these flows via an external redirect
 * (TossPayments) and returns to a fresh document — sessionStorage is the only
 * cache that survives that round-trip.
 *
 *  - `/orders/checkout-sessions` — session creation; user may refresh the
 *    checkout page before paying.
 *  - `/payments/confirm` — invoked by `/checkout/success` after the Toss
 *    redirect; refresh on this page must not generate a new ULID.
 */
const REQUIRE_PERSISTENT_SESSION: ReadonlyArray<{
  method: string;
  pathPattern: RegExp;
}> = [
  { method: 'POST', pathPattern: /^\/orders\/checkout-sessions$/ },
  { method: 'POST', pathPattern: /^\/payments\/confirm$/ },
];

export const requiresPersistentSession = (method: string, path: string): boolean => {
  const m = method.toUpperCase();
  return REQUIRE_PERSISTENT_SESSION.some(
    (r) => r.method === m && r.pathPattern.test(path),
  );
};
